import * as React from 'react';

import { Autocomplete, AutocompleteProps, Popper, styled, ListSubheader, Box, Typography } from '@mui/material';

import { VariableSizeList, ListChildComponentProps } from 'react-window';

import { LinearProgress } from '@mui/material';

import './StylesAutocomplete.css';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style, ...restProps } = props;

    const dataSet = data[index];

    const inlineStyle = {
        ...style,
        cursor: 'pointer',
        top: (style.top as number) + LISTBOX_PADDING
    };

    if (dataSet.hasOwnProperty('group')) {
        return (
            <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
                {dataSet.group}
            </ListSubheader>
        );
    }

    // Filter out unrecognized props before passing them to the Typography component
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isScrolling: _, ...filteredRestProps } = restProps;

    const finalProps = { ...filteredRestProps, ...dataSet.props, style: inlineStyle };

    if (typeof dataSet.props.children === 'string') {
        return (
            <Typography {...finalProps} component="li" sx={{ fontSize: 13 }}>
                {dataSet.props.children}
            </Typography>
        );
    }

    // add props to the same element created in renderOption
    const optionComponent = React.cloneElement(dataSet.props.children, finalProps);

    return optionComponent;
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;

    const itemData: React.ReactElement[] = [];

    (children as React.ReactElement[]).forEach((item: React.ReactElement & { children?: React.ReactElement[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });

    const totalHeight = itemData.reduce((acc, item) => acc + (item.type === 'li' ? 36 : 48), 0);
    const listHeight = Math.min(totalHeight + 18, 300);
    return (
        <div className="scrollAutoCompleteVirtualized" ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={listHeight}
                    width="100%"
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemCount={itemData.length}
                    itemSize={(index) => (itemData[index].type === 'li' ? 36 : 48)}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const StyledPopper = styled(Popper)({
    [`& .MuiAutocomplete-listbox`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0
        }
    }
});

// Dynamic Interface
type VirtualAutocompleteProps<T, Multiple extends boolean | undefined = false> = AutocompleteProps<T, Multiple, false, false> & {};

export default function AutocompleteVirtualized<T, Multiple extends boolean | undefined = false>({
    options,
    onChange,
    value,
    ...rest
}: VirtualAutocompleteProps<T, Multiple>) {
    return (
        <Autocomplete
            {...rest}
            loadingText={<LoadingSelectorData />}
            PopperComponent={StyledPopper}
            ListboxComponent={ListboxComponent}
            options={options}
            onChange={onChange}
            value={value}
        />
    );
}

export const LoadingSelectorData = () => (
    <Box sx={{ p: 1 }}>
        <LinearProgress />
    </Box>
);
